/*
 * smart-header.js
 * Barre d'annonce + header principal : un seul bloc collant, qui se retracte
 * de la hauteur de la barre au scroll descendant et la redonne au scroll
 * montant. Les deux elements sont dans le meme conteneur
 * (.smart-header-group, pose dans layout/theme.liquid), donc ils bougent
 * ensemble par construction : une seule animation, aucun decalage possible.
 *
 * Avant, la barre glissait en transform et le header suivait en top : deux
 * animations distinctes de 0.15s, l'une composee par le GPU et l'autre
 * recalculee a chaque image. Elles se desynchronisaient et l'en-tete se
 * detachait de la barre pendant le scroll.
 *
 * Charge SANS defer juste apres {% sections 'header-group' %} dans
 * layout/theme.liquid : le script mesure la barre avant le premier paint du
 * contenu, donc aucun saut de layout (CLS).
 *
 * Prerequis : sections/header-group.json -> header.settings.sticky_header_type
 * vaut "always" ou "reduce-logo-size". Dans ces deux cas, le StickyHeader
 * natif de Dawn (sections/header.liquid) sort immediatement de hide() /
 * reveal() / reset() : il ne touche plus au top du header. Aucun conflit.
 */
(() => {
  const GROUP_SELECTOR = '.smart-header-group';
  const ANNOUNCEMENT_SELECTOR = '.announcement-bar-section';
  const HEADER_SELECTOR = '.section-header';
  const THRESHOLD = 2;

  let group = null;
  let announcement = null;
  let resizeObserver = null;
  let listenersBound = false;
  let lastScrollY = 0;
  let ticking = false;

  function updateHeight() {
    const height = announcement ? announcement.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--smart-announcement-height', `${height}px`);
  }

  function setCollapsed(collapsed) {
    if (!group) return;
    group.classList.toggle('is-collapsed', collapsed);
  }

  /* Dawn verrouille le scroll quand le menu drawer est ouvert et fige le header
     pendant une recherche predictive. On s'aligne pour ne pas bouger la barre
     sous les doigts de l'utilisateur. */
  function isLocked() {
    const header = document.querySelector(HEADER_SELECTOR);
    if (header && header.classList.contains('menu-open')) return true;
    const body = document.body.classList;
    if (body.contains('overflow-hidden-mobile') || body.contains('overflow-hidden-tablet')) return true;
    const predictiveSearch = document.querySelector('predictive-search');
    return Boolean(predictiveSearch && predictiveSearch.isOpen);
  }

  function handleScroll() {
    ticking = false;
    if (!group || !announcement || isLocked()) return;

    const currentScrollY = Math.max(window.scrollY, 0);
    const delta = currentScrollY - lastScrollY;

    if (currentScrollY <= THRESHOLD) {
      setCollapsed(false);
    } else if (delta > THRESHOLD) {
      setCollapsed(true);
    } else if (delta < -THRESHOLD) {
      setCollapsed(false);
    } else {
      // Sous le seuil : on garde lastScrollY pour cumuler les micro-scrolls,
      // sinon un scroll tres lent ne declencherait jamais rien.
      return;
    }

    lastScrollY = currentScrollY;
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(handleScroll);
  }

  function disconnectObserver() {
    if (!resizeObserver) return;
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  /* Idempotent : rejoue a chaque shopify:section:load/unload/reorder de
     l'editeur de theme, ou la barre d'annonce peut etre remplacee ou supprimee. */
  function sync() {
    group = document.querySelector(GROUP_SELECTOR);
    if (!group) return false;

    disconnectObserver();
    announcement = group.querySelector(ANNOUNCEMENT_SELECTOR);

    // Barre supprimee dans l'editeur : plus rien a retracter, le groupe reste
    // colle en haut.
    group.classList.toggle('has-announcement', Boolean(announcement));

    updateHeight();
    setCollapsed(false);
    lastScrollY = Math.max(window.scrollY, 0);

    if (announcement && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(announcement);
    }

    if (!listenersBound) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', updateHeight, { passive: true });
      window.addEventListener('orientationchange', updateHeight, { passive: true });
      listenersBound = true;
    }

    return true;
  }

  // Le script est place apres le header-group : les deux elements existent deja.
  // Le fallback couvre le cas ou il serait deplace ou charge en defer.
  if (!sync()) {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  }

  document.addEventListener('shopify:section:load', sync);
  document.addEventListener('shopify:section:unload', sync);
  document.addEventListener('shopify:section:reorder', sync);
})();
